import React, { useMemo, useState } from "react";
import {
  GraduationCap,
  UserCheck,
  UserX,
  BadgeIndianRupee,
  Clock3,
  CheckCircle2,
  AlertCircle,
  Users,
  TrendingUp,
} from "lucide-react";
import { isInFilter } from "../utils/dateFilter";

const FILTER_TABS = ["Today", "Yesterday", "Weekly", "Monthly", "All Time", "Custom Date"];

/** Default admission fee used when creating invoices (see admission.route.js). */
export const DEFAULT_ADMISSION_FEE = 1000;

export function isAdmissionActive(status) {
  return String(status || "").trim() === "Active";
}

export function isAdmissionInactive(status) {
  return String(status || "").trim() === "Deactive";
}

export function isPaymentReceived(status) {
  return String(status || "").trim() === "Paid";
}

export function isPaymentPending(status) {
  return String(status || "").trim() === "Active";
}

export function isPaymentFailed(status) {
  return String(status || "").trim() === "Failed";
}

function getFilterLabel(filter) {
  if (typeof filter === "string") return filter;
  if (filter?.type === "Custom Date" && filter.from && filter.to) {
    return `${filter.from} to ${filter.to}`;
  }
  return "Selected period";
}

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const SummaryDateFilter = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState(
    typeof value === "string" ? value : value?.type === "Custom Date" ? "Custom Date" : "All Time"
  );
  const [showCustom, setShowCustom] = useState(activeTab === "Custom Date");
  const [tempRange, setTempRange] = useState({
    from: value?.from || "",
    to: value?.to || "",
  });

  const handleTabClick = (tab) => {
    if (tab === "Custom Date") {
      setActiveTab("Custom Date");
      setShowCustom(true);
      return;
    }
    setActiveTab(tab);
    setShowCustom(false);
    onChange(tab);
  };

  const handleApplyCustom = () => {
    if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
    onChange({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
    setShowCustom(false);
  };

  return (
    <div className="admission-summary__date-filter">
      <div className="admission-summary__date-pills dash-filter-scroll">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => handleTabClick(tab)}
            className={`admission-summary__date-pill${
              activeTab === tab ? " admission-summary__date-pill--active" : ""
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {showCustom && (
        <div className="admission-summary__custom-range">
          <div className="admission-summary__custom-fields">
            <label className="admission-summary__custom-label">
              From
              <input
                type="date"
                value={tempRange.from}
                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
                className="admission-summary__custom-input"
              />
            </label>
            <label className="admission-summary__custom-label">
              To
              <input
                type="date"
                value={tempRange.to}
                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
                className="admission-summary__custom-input"
              />
            </label>
          </div>
          <div className="admission-summary__custom-actions">
            <button
              type="button"
              onClick={() => {
                setShowCustom(false);
                if (typeof value === "string") setActiveTab(value);
              }}
              className="admission-summary__custom-btn admission-summary__custom-btn--ghost"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCustom}
              disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
              className="admission-summary__custom-btn admission-summary__custom-btn--primary"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, hint, tone = "default", icon: Icon, active, onClick }) => {
  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`admission-summary__metric-card${
        active ? " admission-summary__metric-card--active" : ""
      }${onClick ? " admission-summary__metric-card--clickable" : ""}`}
      aria-pressed={onClick ? active : undefined}
    >
      <div className="admission-summary__metric-head">
        <p className="admission-summary__metric-label">{label}</p>
        {Icon ? <Icon size={14} className="admission-summary__metric-icon" /> : null}
      </div>
      <p className={`admission-summary__metric-value admission-summary__metric-value--${tone}`}>
        {value}
      </p>
      {hint ? <p className="admission-summary__metric-hint">{hint}</p> : null}
    </Tag>
  );
};

export default function AdmissionPageSummary({
  admissions = [],
  period = "All Time",
  onPeriodChange,
  statusFilter = "all",
  onStatusFilterChange,
}) {
  const summary = useMemo(() => {
    const periodRows = (Array.isArray(admissions) ? admissions : []).filter((row) =>
      isInFilter(row.createdAt, period)
    );

    const activeCount = periodRows.filter((r) => isAdmissionActive(r.status)).length;
    const inactiveCount = periodRows.filter((r) => isAdmissionInactive(r.status)).length;
    const paidCount = periodRows.filter((r) => isPaymentReceived(r.status)).length;
    const pendingCount = periodRows.filter((r) => isPaymentPending(r.status)).length;
    const failedCount = periodRows.filter((r) => isPaymentFailed(r.status)).length;
    const total = periodRows.length;
    const statusTotal = total || 1;

    const receivedAmount = paidCount * DEFAULT_ADMISSION_FEE;
    const pendingAmount = pendingCount * DEFAULT_ADMISSION_FEE;
    const collectionRate =
      paidCount + pendingCount > 0
        ? Math.round((paidCount / (paidCount + pendingCount)) * 100)
        : paidCount > 0
          ? 100
          : 0;

    const unbatched = periodRows.filter((r) => !r.batch_id).length;
    const centers = new Set(
      periodRows.map((r) => String(r.center || "").trim()).filter(Boolean)
    ).size;

    return {
      filterLabel: getFilterLabel(period),
      total,
      activeCount,
      inactiveCount,
      paidCount,
      pendingCount,
      failedCount,
      receivedAmount,
      pendingAmount,
      collectionRate,
      activePct: Math.round((activeCount / statusTotal) * 100),
      inactivePct: Math.round((inactiveCount / statusTotal) * 100),
      paidPct: Math.round((paidCount / statusTotal) * 100),
      pendingPct: Math.round((pendingCount / statusTotal) * 100),
      unbatched,
      centers,
    };
  }, [admissions, period]);

  const switchFilter = (next) => {
    if (onStatusFilterChange) onStatusFilterChange(next);
  };

  return (
    <section className="admission-page-summary">
      <div className="admission-summary__header">
        <div>
          <h2 className="admission-summary__title">Admission Summary</h2>
          <p className="admission-summary__subtitle">
            Active vs inactive students and payment health — {summary.filterLabel}
          </p>
        </div>
        <SummaryDateFilter value={period} onChange={onPeriodChange} />
      </div>

      <div className="admission-summary__grid">
        <div className="admission-summary__panel admission-summary__panel--enrollment">
          <div className="admission-summary__panel-head">
            <div className="admission-summary__panel-icon admission-summary__panel-icon--indigo">
              <GraduationCap size={16} />
            </div>
            <h3 className="admission-summary__panel-title">Enrollment</h3>
          </div>
          <div className="admission-summary__metrics dash-metric-scroll dash-metric-scroll--admission">
            <MetricCard
              label="Total Admissions"
              value={summary.total}
              hint="In selected period"
              icon={Users}
              active={statusFilter === "all"}
              onClick={() => switchFilter("all")}
            />
            <MetricCard
              label="Active"
              value={summary.activeCount}
              hint={`${summary.activePct}% of total`}
              icon={UserCheck}
              tone="success"
              active={statusFilter === "Active"}
              onClick={() => switchFilter("Active")}
            />
            <MetricCard
              label="Inactive"
              value={summary.inactiveCount}
              hint={`Deactive · ${summary.inactivePct}%`}
              icon={UserX}
              tone={summary.inactiveCount > 0 ? "warn" : "default"}
              active={statusFilter === "Deactive"}
              onClick={() => switchFilter("Deactive")}
            />
            <MetricCard
              label="Failed"
              value={summary.failedCount}
              hint="Payment failed"
              icon={AlertCircle}
              tone={summary.failedCount > 0 ? "danger" : "default"}
              active={statusFilter === "Failed"}
              onClick={() => switchFilter("Failed")}
            />
          </div>
        </div>

        <div className="admission-summary__panel admission-summary__panel--payment">
          <div className="admission-summary__panel-head">
            <div className="admission-summary__panel-icon admission-summary__panel-icon--violet">
              <BadgeIndianRupee size={16} />
            </div>
            <h3 className="admission-summary__panel-title">Payment</h3>
          </div>
          <div className="admission-summary__metrics dash-metric-scroll dash-metric-scroll--admission">
            <MetricCard
              label="Received"
              value={summary.paidCount}
              hint={formatCurrency(summary.receivedAmount)}
              icon={CheckCircle2}
              tone="success"
              active={statusFilter === "Paid"}
              onClick={() => switchFilter("Paid")}
            />
            <MetricCard
              label="Pending"
              value={summary.pendingCount}
              hint={formatCurrency(summary.pendingAmount)}
              icon={Clock3}
              tone={summary.pendingCount > 0 ? "warn" : "default"}
              active={statusFilter === "Active"}
              onClick={() => switchFilter("Active")}
            />
            <MetricCard
              label="Collection Rate"
              value={`${summary.collectionRate}%`}
              hint="Paid vs pending students"
              icon={TrendingUp}
            />
            <MetricCard
              label="No Batch"
              value={summary.unbatched}
              hint={`${summary.centers} center(s)`}
              icon={GraduationCap}
              tone={summary.unbatched > 0 ? "warn" : "default"}
            />
          </div>
        </div>
      </div>

      <div className="admission-summary__breakdown">
        <p className="admission-summary__breakdown-title">Status breakdown</p>
        <div className="admission-summary__breakdown-bar">
          {summary.activeCount > 0 && (
            <button
              type="button"
              title={`Active: ${summary.activeCount}`}
              onClick={() => switchFilter("Active")}
              className="admission-summary__bar-segment admission-summary__bar-segment--active"
              style={{ width: `${(summary.activeCount / (summary.total || 1)) * 100}%` }}
            />
          )}
          {summary.inactiveCount > 0 && (
            <button
              type="button"
              title={`Inactive: ${summary.inactiveCount}`}
              onClick={() => switchFilter("Deactive")}
              className="admission-summary__bar-segment admission-summary__bar-segment--inactive"
              style={{ width: `${(summary.inactiveCount / (summary.total || 1)) * 100}%` }}
            />
          )}
          {summary.paidCount > 0 && (
            <button
              type="button"
              title={`Paid: ${summary.paidCount}`}
              onClick={() => switchFilter("Paid")}
              className="admission-summary__bar-segment admission-summary__bar-segment--paid"
              style={{ width: `${(summary.paidCount / (summary.total || 1)) * 100}%` }}
            />
          )}
          {summary.failedCount > 0 && (
            <button
              type="button"
              title={`Failed: ${summary.failedCount}`}
              onClick={() => switchFilter("Failed")}
              className="admission-summary__bar-segment admission-summary__bar-segment--failed"
              style={{ width: `${(summary.failedCount / (summary.total || 1)) * 100}%` }}
            />
          )}
        </div>
        <div className="admission-summary__breakdown-legend">
          <button type="button" onClick={() => switchFilter("Active")}>
            <i className="admission-summary__dot admission-summary__dot--active" />
            Active ({summary.activeCount})
          </button>
          <button type="button" onClick={() => switchFilter("Deactive")}>
            <i className="admission-summary__dot admission-summary__dot--inactive" />
            Inactive ({summary.inactiveCount})
          </button>
          <button type="button" onClick={() => switchFilter("Paid")}>
            <i className="admission-summary__dot admission-summary__dot--paid" />
            Paid ({summary.paidCount})
          </button>
          <button type="button" onClick={() => switchFilter("Failed")}>
            <i className="admission-summary__dot admission-summary__dot--failed" />
            Failed ({summary.failedCount})
          </button>
        </div>
      </div>

      <div className="admission-summary__payment-bar-wrap">
        <p className="admission-summary__breakdown-title">Payment received vs pending</p>
        <div className="admission-summary__breakdown-bar">
          {summary.receivedAmount > 0 && (
            <span
              className="admission-summary__bar-segment admission-summary__bar-segment--paid"
              style={{
                width: `${Math.max(
                  4,
                  Math.round(
                    (summary.receivedAmount /
                      Math.max(summary.receivedAmount + summary.pendingAmount, 1)) *
                      100
                  )
                )}%`,
              }}
              title={`Received: ${formatCurrency(summary.receivedAmount)}`}
            />
          )}
          {summary.pendingAmount > 0 && (
            <span
              className="admission-summary__bar-segment admission-summary__bar-segment--pending"
              style={{
                width: `${Math.max(
                  4,
                  Math.round(
                    (summary.pendingAmount /
                      Math.max(summary.receivedAmount + summary.pendingAmount, 1)) *
                      100
                  )
                )}%`,
              }}
              title={`Pending: ${formatCurrency(summary.pendingAmount)}`}
            />
          )}
        </div>
        <div className="admission-summary__breakdown-legend">
          <span>
            <i className="admission-summary__dot admission-summary__dot--paid" />
            Received {formatCurrency(summary.receivedAmount)}
          </span>
          <span>
            <i className="admission-summary__dot admission-summary__dot--pending" />
            Pending {formatCurrency(summary.pendingAmount)}
          </span>
        </div>
      </div>

      <div className="admission-summary__insights">
        <div className="admission-summary__insight">
          <UserCheck size={14} />
          <span>
            {summary.activeCount > 0
              ? `${summary.activeCount} active student(s) — ${formatCurrency(summary.pendingAmount)} payment still pending.`
              : "No active admissions awaiting payment in this period."}
          </span>
        </div>
        <div className="admission-summary__insight">
          <CheckCircle2 size={14} />
          <span>
            {summary.paidCount > 0
              ? `${summary.paidCount} paid admission(s) — ${formatCurrency(summary.receivedAmount)} received.`
              : "No paid admissions in this period yet."}
          </span>
        </div>
        <div className="admission-summary__insight">
          <UserX size={14} />
          <span>
            {summary.inactiveCount > 0
              ? `${summary.inactiveCount} inactive (deactive) admission(s) may need follow-up.`
              : "No inactive admissions in this period."}
          </span>
        </div>
      </div>
    </section>
  );
}
